import { differenceInDays } from 'src/app/shared/utils/dates';

import type { HeroActivityType, HeroResult, SpotlightContent } from './types';

export const NEW_PETITION_DAYS_THRESHOLD = 14;

export const ACTIVITY_TYPES: readonly HeroActivityType[] = [
	'dmResponse',
	'endorsement',
	'mediaMention',
	'starterUpdate',
];

/**
 * Fallback priority for activity types that don't have a date available.
 * Lower number = higher priority. Only used when timestamps can't determine the order.
 */
export const UNDATED_ACTIVITY_PRIORITY: Record<HeroActivityType, number> = {
	endorsement: 0,
	dmResponse: 1,
	mediaMention: 2,
	starterUpdate: 3,
};

export function parseDate(dateString: string): number | null {
	const ms = new Date(dateString).getTime();
	return Number.isNaN(ms) ? null : ms;
}

export function getDaysAgo(now: Date, dateString: string): number | null {
	const date = new Date(dateString);
	if (Number.isNaN(date.getTime())) return null;
	return differenceInDays(now, date);
}

/**
 * Sorts all candidates by recency first, then by undated priority as a fallback.
 * Activities with a known timestamp are sorted chronologically (most recent first).
 * Activities without a timestamp use the provided priority map and are ranked last.
 */
export function sortCandidates(
	candidates: Array<[HeroActivityType, HeroResult]>,
	undatedPriority: Record<HeroActivityType, number>,
): SpotlightContent[] {
	if (candidates.length === 0) return [];

	const sorted = [...candidates].sort(([typeA, a], [typeB, b]) => {
		if (a.timestamp !== null && b.timestamp !== null) return b.timestamp - a.timestamp;
		if (a.timestamp !== null) return -1;
		if (b.timestamp !== null) return 1;
		return undatedPriority[typeA] - undatedPriority[typeB];
	});

	return sorted.map(([, result]) => result.content);
}

/**
 * Returns the highest-priority candidate, or null if none exist.
 */
export function pickWinner(
	candidates: Array<[HeroActivityType, HeroResult]>,
	undatedPriority: Record<HeroActivityType, number>,
): SpotlightContent | null {
	return sortCandidates(candidates, undatedPriority)[0] ?? null;
}

/**
 * First petition tag (in `tagsConnection` order) whose name or slug appears in the search Topics
 * facet allowlist (same facet values as the search page topic chips).
 */
export function getSpotlightCategoryFromSearchTopicFacets(
	petitionTags:
		| {
				readonly nodes: ReadonlyArray<{
					readonly id: string;
					readonly name: string;
					readonly slug: string;
				}>;
		  }
		| null
		| undefined,
	searchTopicFacetValues: readonly string[] | null | undefined,
): string | null {
	const normalizeTopicValue = (value: string): string =>
		value
			.trim()
			.toLowerCase()
			.replace(/-[a-z]{2}-[a-z]{2}$/i, '');

	const allowlist = new Set((searchTopicFacetValues ?? []).map(normalizeTopicValue));
	for (const node of petitionTags?.nodes ?? []) {
		const normalizedName = normalizeTopicValue(node.name);
		const normalizedSlug = normalizeTopicValue(node.slug);
		if (allowlist.has(normalizedName) || allowlist.has(normalizedSlug)) {
			return node.name;
		}
	}
	return null;
}
